import React, { useState, useEffect, useRef } from 'react';
import { useStripeCheckoutStatus } from '@/hooks/useStripeCheckoutStatus';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/lib/auth';
import { createBookingAndRedirectToStripe } from '@/lib/stripe';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Users, Clock, ArrowDown, Play, Pause, Volume2, VolumeX, Utensils, Music, Wine, Instagram } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { MetaTags } from '@/components/MetaTags';

export function ArtHouseExperience() {
  // Handle Stripe checkout status when returning from payment
  useStripeCheckoutStatus({
    customSuccessMessage: "Your ArtHouse booking is confirmed! Please check your email for details.",
    customCancelMessage: "Your ArtHouse booking was canceled. No payment was processed."
  });
  
  const { user } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [freezeFrameShowing, setFreezeFrameShowing] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Mark page as loaded
  useEffect(() => {
    setPageLoaded(true);
  }, []);
  
  // Initialize Google Maps
  useEffect(() => {
    const mapElement = document.getElementById('venue-map');
    if (!mapElement || !window.google?.maps) return;

    // New York coordinates
    const location = { lat: 40.7831, lng: -73.9712 };
    const map = new google.maps.Map(mapElement, {
      center: location,
      zoom: 15,
      mapTypeId: "roadmap",
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
        style: google.maps.ZoomControlStyle.SMALL
      }
    });

    // Add marker for ArtHouse
    new google.maps.Marker({
      position: location,
      map,
      title: "ArtHouse by Sedef Gali"
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageLoaded) return;

    // Preload video data
    video.preload = "auto";
    
    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      
      // Start the video 1.5 seconds after the page loads
      setTimeout(() => {
        setFreezeFrameShowing(false);
        video.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(err => {
            console.warn('Autoplay failed:', err);
            setIsPlaying(false);
          });
      }, 1500);
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!freezeFrameShowing) { // Only auto-play if freeze frame is not showing
          video.play().catch(err => console.warn('Visibility play failed:', err));
          setIsPlaying(true);
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, [pageLoaded]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    // If we're showing the freeze frame, cancel it and play immediately
    if (freezeFrameShowing) {
      setFreezeFrameShowing(false);
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handlePurchaseClick = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    try {
      // Use the generalized function to create a booking and redirect to Stripe
      const experienceId = "d8bd55de-3774-4415-9e2b-19896b4a2df0";
      const priceId = "price_1REuAqIixwSwRAyJGiSTK7QP";
      const participantCount = 1;
      const totalAmount = 90;
      
      await createBookingAndRedirectToStripe(
        experienceId,
        priceId,
        participantCount,
        totalAmount,
        '/experiences/arthouse' // Return to this experience page after checkout
      );
    } catch (error) {
      console.error('Failed to create booking:', error);
    }
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags 
        title="Spring New Dinner Party | ArtHouse by Sedef Gali | rendezvous"
        description="A dreamy spring night at ArtHouse with bottomless drinks, shared food, gallery tours, and anonymous dream sharing."
        image="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/Background%20image.jpg"
        url="https://letsrendezvous.co/experiences/arthouse"
      />

      {/* Hero Section with Video/Image */}
      <div className="relative w-full h-[100vh] overflow-hidden pt-10">
        {/* Hero Video Container with Center Alignment */}
        <div className="absolute inset-0 top-20 flex justify-center items-center bg-earth-900 overflow-hidden">
          {/* Poster Image/Freeze Frame */}
          <div 
            className={`absolute inset-0 z-10 transition-opacity duration-500 ${freezeFrameShowing ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/Background%20image.jpg"
              alt="ArtHouse Dinner Party"
              className="w-full h-full object-cover object-center"
            />
          </div>
          
          {/* Video Element (placeholder - replace with actual video URL) */}
          <video
            ref={videoRef}
            src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/First%20Dinner%20Widescreen%20Video%20-%201741632648496.mp4"
            className={`w-full h-full object-cover transition-opacity duration-1000 ${freezeFrameShowing ? 'opacity-0' : 'opacity-100'}`}
            muted={isMuted}
            loop
            playsInline
            poster="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/First%20Dinner%20Widescreen%20Video%20-%201741632648496.mp4"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />

          {/* Video Controls */}
          <div className="absolute bottom-10 right-10 flex items-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors cursor-pointer"
            >
              {isPlaying && !freezeFrameShowing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
            <button
              onClick={handleToggleMute}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors cursor-pointer"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="absolute inset-0 top-20 flex flex-col items-center justify-end px-4 text-center pb-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display mb-6">Spring Dinner Party</h1>
            <p className="text-xl md:text-2xl text-sand-200 mb-8">A dreamy spring night at ArtHouse by Sedef Gali</p>
          </motion.div>

          {/* Scroll Down Indicator */}
          <motion.div
            className="absolute bottom-12 left-0 right-0 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center text-sand-300 hover:text-sand-100 transition-colors"
            >
              <span className="text-sm uppercase tracking-widest mb-2">Discover</span>
              <ArrowDown className="h-6 w-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Reserve Button Section - Now outside the hero */}
      <div className="relative bg-earth-900 py-12 text-center">
        <Button 
          size="lg"
          onClick={handlePurchaseClick}
          className="text-lg px-8 py-4"
        >
          Buy Your Ticket
        </Button>
      </div>

      {/* Content Section */}
      <div id="content-section" className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          {/* Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="about" className="space-y-8">
                <TabsList className="w-full">
                  <TabsTrigger value="about">About</TabsTrigger>
                  <TabsTrigger value="experience">The Experience</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                </TabsList>
                
                <TabsContent value="about" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">About ArtHouse</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                      ArtHouse by Sedef Gali is a unique art gallery and event space where creative minds gather for immersive experiences. Located in the heart of New York, this vibrant space combines art, conversation, and culinary delights in a welcoming atmosphere designed to spark connection.
                    </p>
                    <p>
                      Sedef Gali, the founder of ArtHouse, is a multidisciplinary artist and curator with a passion for creating spaces where art, food, and community intersect. Through her curated experiences, she invites guests to explore creativity in all its forms while forming meaningful connections with fellow attendees.
                    </p>
                    
                    <div className="pt-4">
                      <h3 className="text-xl font-display mb-3">The Spring Dinner Party</h3>
                      <p>
                        You're invited to a dreamy spring night at the ArtHouse—a gathering of curious souls, delicious bites, and flowing conversation. Join us for our Spring New Dinner Party, where drinks are bottomless, the food is shared family-style at our open communal table, and strangers become friends through art and anonymous dream sharing.
                      </p>
                      <p className="mt-4">
                        Unlike traditional dinner parties, this is an interactive experience where you'll wander through the gallery, engage in meaningful conversations about beauty, life, and longing, and share dreams anonymously with other guests, creating a tapestry of collective imagination.
                      </p>
                    </div>
                  
                    {/* Media Gallery - Added to replace the gallery tab */}
                    <div className="mt-8">
                      <h3 className="text-xl font-display mb-6">Gallery</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/01ef4ef25d27080f5d9c5fbb4ad506eb6eef4bfa93.jpg" 
                          alt="ArtHouse Gathering" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0005.jpg" 
                          alt="ArtHouse Setting" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0006.jpg" 
                          alt="ArtHouse Experience" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0007.jpg" 
                          alt="ArtHouse Atmosphere" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0008.jpg" 
                          alt="ArtHouse Dinner" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0009.jpg" 
                          alt="ArtHouse Event" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0010.jpg" 
                          alt="ArtHouse Gathering" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0011.jpg" 
                          alt="ArtHouse Party" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0012.jpg" 
                          alt="ArtHouse Interaction" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0013.jpg" 
                          alt="ArtHouse Experience" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/IMG-20250424-WA0014.jpg" 
                          alt="ArtHouse Setting" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/image%20gallery/IMG-20250424-WA0015.jpg" 
                          alt="ArtHouse Ambiance" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/IMG-20250424-WA0016.jpg" 
                          alt="ArtHouse Setup" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/IMG-20250424-WA0017.jpg" 
                          alt="ArtHouse Interior" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="experience" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">The Experience</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                      The Spring Dinner Party is not your typical seated dinner. It's a social, standing experience designed around movement, conversation, and connection. Throughout the night, you'll enjoy:
                    </p>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg mt-6">
                      <h3 className="text-xl font-display mb-4">What to Expect</h3>
                      <ul className="list-disc list-inside space-y-3">
                        <li>An open bar all night long with craft cocktails and select wines</li>
                        <li>Communal food stations with seasonal bites and shared plates</li>
                        <li>A guided tour of the current gallery exhibition</li>
                        <li>Anonymous dream sharing activities that connect guests in unexpected ways</li>
                        <li>Ambient music and evolving soundscapes throughout the evening</li>
                        <li>Meaningful conversations with fascinating attendees</li>
                      </ul>
                    </div>
                    
                    <p className="mt-4">
                      Unlike traditional dining experiences where you're seated for hours, the Spring New Dinner Party encourages movement and exploration. You'll flow between food stations, the gallery spaces, and intimate conversation nooks, creating your own unique journey throughout the evening.
                    </p>
                    
                    <p className="mt-2">
                      The format is designed to foster authentic connections. Through structured dream-sharing activities, you'll engage with other guests on a deeper level while maintaining the playfulness of anonymity.
                    </p>
                  </div>
                </TabsContent>
                
                <TabsContent value="location" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Location</h2>
                  <div className="space-y-6">
                    <p className="text-sand-300">
                      Located in a charming space in the heart of New York City, ArtHouse provides an intimate setting for our Spring New Dinner Party. The venue combines gallery spaces with open areas perfect for conversation and connection.
                    </p>
                    <p className="text-sand-300 mb-6">
                      The address will be shared with confirmed guests. The location is easily accessible by public transportation and rideshare services.
                    </p>
                    
                    <div id="venue-map" className="w-full h-[300px] rounded-lg overflow-hidden"></div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="sticky top-24 space-y-6">
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-display">Price</h3>
                    <div className="text-2xl font-display">$90 (incl tax)</div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sand-300">
                      <Clock className="h-5 w-5 text-sand-400" />
                      <span>8:00 PM - 1:00 AM</span>
                    </div>
                    
                    <Button 
                      onClick={handlePurchaseClick}
                      className="w-full mt-4"
                    >
                      Buy Your Ticket
                    </Button>
                  </div>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Host</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/6232170cf8866e647efd95ab/6e91a6a6-7618-4a71-88a4-aa46c0a0c791/Sedef+Profile.jpeg?format=1500w" 
                      alt="Sedef Gali"
                      className="w-16 h-16 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://api.dicebear.com/7.x/initials/svg?seed=SG&backgroundColor=1d1918&textColor=e8e4dc";
                      }}
                    />
                    <div>
                      <p className="text-sand-100 font-medium">Sedef Gali</p>
                      <p className="text-sand-400 text-sm">Artist & Curator</p>
                    </div>
                  </div>
                  <p className="text-sand-300 text-sm mb-4">
                    Sedef Gali is an interdisciplinary artist and the founder of ArtHouse, creating spaces where art, food, and community intersect in meaningful ways. Her events blend artistic exploration with culinary experiences.
                  </p>
                  <div className="flex flex-col space-y-2">
                    <a 
                      href="https://www.instagram.com/sedefgali" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors"
                    >
                      <Instagram className="h-4 w-4" />
                      <span>@sedefgali</span>
                    </a>
                    <a 
                      href="https://www.instagram.com/sedefgaliarthouse" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors"
                    >
                      <Instagram className="h-4 w-4" />
                      <span>@sedefgaliarthouse</span>
                    </a>
                  </div>
                </div>
                
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Wine className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Open bar all night with craft cocktails</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Utensils className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Communal food at an open table</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Music className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Curated music and ambiance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Anonymous dream sharing activities</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="mt-24 bg-earth-800/50 p-8 rounded-lg text-center">
            <h2 className="text-3xl font-display mb-6">Join Our Spring Gathering</h2>
            <p className="text-sand-300 mb-8 max-w-2xl mx-auto">
              Experience an evening where art, conversation, and culinary delights blend into a unique communal experience. Limited spots available to ensure an intimate gathering.
            </p>
            <Button 
              size="lg"
              onClick={handlePurchaseClick}
              className="text-lg px-8 py-4"
            >
              Buy Your Ticket
            </Button>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={location.pathname}
      />
    </div>
  );
}

export default ArtHouseExperience;